// Status vocabulary and button rules. Review/approve are identity-based (named
// reviewer/approver), not permission-based — the server enforces this too.

// rbacapp codes: veh_view, veh_act, veh_view_all, veh_dispatch.
// Review/approve need none of these — being named is the authorization.
export const VEH_VIEW_PERMISSIONS = ['rbacapp.veh_view', 'rbacapp.veh_act', 'rbacapp.veh_view_all', 'rbacapp.veh_dispatch'];
export const VEH_ACT_PERMISSIONS = ['rbacapp.veh_act'];
export const VEH_VIEW_ALL_PERMISSIONS = ['rbacapp.veh_view_all'];
export const VEH_DISPATCH_PERMISSIONS = ['rbacapp.veh_dispatch'];

export const STATUS_LABELS = {
    draft: 'Draft',
    pending_review: 'Pending Review',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    rejected: 'Rejected',
    assigned: 'Vehicle Assigned',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

export const STATUS_BADGE = {
    draft: 'bg-bg-light text-text-secondary border border-border',
    pending_review: 'bg-sky/10 text-sky border border-sky/30',
    pending_approval: 'bg-amber-500/10 text-amber-500 border border-amber-500/30',
    approved: 'bg-success/10 text-success border border-success/30',
    rejected: 'bg-danger/10 text-danger border border-danger/30',
    assigned: 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/30',
    in_progress: 'bg-teal-500/10 text-teal-500 border border-teal-500/30',
    completed: 'bg-success/20 text-success border border-success/40',
    cancelled: 'bg-bg-light text-text-secondary border border-border',
};

export const statusLabel = (request) =>
    request?.status_display || STATUS_LABELS[request?.status] || request?.status || '-';

export const statusBadgeClass = (status) =>
    STATUS_BADGE[status] || 'bg-bg-light text-text-secondary border border-border';

// Terminal states — view and print only.
export const isClosed = (request) => ['completed', 'cancelled'].includes(request?.status);

// Prefers backend booleans over comparing employee ids.
export function getActorFlags(request, user) {
    if (!request || !user) return { isRequester: false, isReviewer: false, isApprover: false };

    const employeeId = user.employeeId;
    const matches = (field) => !!(field && employeeId && String(field) === String(employeeId));

    return {
        isRequester: typeof request.is_requester === 'boolean' ? request.is_requester : matches(request.requester),
        isReviewer: typeof request.can_i_review === 'boolean' ? request.can_i_review : matches(request.reviewer),
        isApprover: typeof request.can_i_approve === 'boolean' ? request.can_i_approve : matches(request.approver),
    };
}

// canAct = veh_act, canDispatch = veh_dispatch; review/approve are identity-only.
export function getAvailableActions(request, user, { canAct = false, canDispatch = false } = {}) {
    if (!request) return [];

    const { isRequester, isReviewer, isApprover } = getActorFlags(request, user);
    const owns = isRequester && canAct;
    const status = request.status;
    const actions = [];

    switch (status) {
        case 'draft':
            if (owns) actions.push('edit', 'submit', 'delete');
            break;
        case 'pending_review':
            if (isReviewer) actions.push('review', 'reject');
            if (owns) actions.push('cancel');
            break;
        case 'pending_approval':
            if (isApprover) actions.push('approve', 'reject');
            if (owns) actions.push('cancel');
            break;
        case 'approved':
            if (canDispatch) actions.push('assign');
            if (owns) actions.push('cancel');
            break;
        case 'rejected':
            if (owns) actions.push('edit', 'submit');
            break;
        case 'assigned':
            if (canDispatch) actions.push('dispatch_trip', 'complete');
            if (owns) actions.push('cancel');
            break;
        case 'in_progress':
            if (canDispatch) actions.push('complete');
            break;
        default:
            break;
    }

    return actions;
}

// Fallback for the badge/queue when awaiting_my_action isn't available.
export function isAwaitingMe(request, user, { canDispatch = false } = {}) {
    const { isReviewer, isApprover } = getActorFlags(request, user);
    if (isReviewer && request?.status === 'pending_review') return true;
    if (isApprover && request?.status === 'pending_approval') return true;
    if (canDispatch && request?.status === 'approved') return true;
    return false;
}

// ─── SHARED HELPERS ──────────────────────────────────────────────────────────

// Shared with IT support — lives in lib/workflowUtils, re-exported here.
export {
    classifyWorkflowError,
    isForbidden,
    withoutEmpty,
    formatDate,
    formatDateTime,
    formatTime,
    dataTableStyles,
} from '../../lib/workflowUtils';
